import { PayloadAction, createSlice } from "@reduxjs/toolkit"
import ChatHelpers from "../lib/chatHelpers"
import { PURGE } from "redux-persist"
import { captureError } from "../ErrorHandlers"
import { ChatWithMessages } from "./chatsSliceSelectors"

export type Reference = {
  type: "address"
  begin: number
  end: number
}

export type Participant = {
  name: string
  firstName?: string
  lastName?: string
  jid: string
  phone?: string
  plates?: string
  role: string
  photo?: {
    data?: string
    type?: string
    url?: string
  }
  description?: string
  preferredLanguage?: string
  disabled?: boolean
}

export type ChatConnectionStatus = "connected" | "disconnected" | "failed"

export type Chat = {
  active: boolean
  jid: string
  name: string
  connectionStatus?: ChatConnectionStatus
  room?: boolean
  /** Opened from the dispatcher view without a bookmark: read and reply, but
   * the chat leaves the store as soon as the user moves on. Persisted state can
   * still carry one after a crash, hence dropPeekedChats on start-up. */
  peek?: boolean
  participants: Participant[]
  draftMessage: string
  groups: string[]
}

export type SimpleChat = Pick<
  Chat,
  "jid" | "name" | "draftMessage" | "active" | "groups"
>

type ChangeActiveChatAction = {
  activeChatJid: string
}

export type UpsertChatAction = {
  chat: Pick<Chat, "jid" | "name" | "room"> & { peek?: boolean }
}

type RemoveChatAction = {
  jid: string
}

export type RefreshChatsAction = {
  chats: Chat[]
}

export type AddParticipantAction = {
  chatJid: string
  participant: Participant
}

type UpdateDraftMessage = {
  chatJid: string
  draftMessage: string
}

type UpdateChatNameAction = {
  chatJid: string
  name: string
}

type ChangeActiveChatToFirstNotArchivedDriverAction = {
  archivedChats: string[]
}

export type ChatsState = Chat[]

const initialState: ChatsState = []

export const buildChat = (chat: Partial<Chat>): Chat => {
  return {
    name: "",
    jid: "",
    active: false,
    connectionStatus: undefined,
    participants: [],
    draftMessage: "",
    groups: [],
    ...chat,
  }
}

export const buildChatWithMessages = (
  chatWithMessages: Partial<ChatWithMessages>,
): ChatWithMessages => {
  return {
    name: "",
    jid: "",
    messages: [],
    ...chatWithMessages,
  }
}

export const chatsSlice = createSlice({
  name: "chats",
  initialState,
  reducers: {
    updateDraftMessage: (state, action: PayloadAction<UpdateDraftMessage>) => {
      const chatIndex = ChatHelpers.findChatIndex(action.payload.chatJid, state)

      state[chatIndex].draftMessage = action.payload.draftMessage

      if (chatIndex !== 0 && action.payload.draftMessage.length > 0) {
        // moveChatToTheTop(chatIndex, state)
      }
    },
    addOrUpdateParticipant: (
      state,
      action: PayloadAction<AddParticipantAction>,
    ) => {
      const chatIndex = state.findIndex(
        (chat) => chat.jid === action.payload.chatJid,
      )

      if (chatIndex === -1)
        throw `Action addDriver: can not find chat with jid: ${action.payload.chatJid}`

      if (!state[chatIndex].participants) state[chatIndex].participants = []

      const participantIndex = state[chatIndex].participants.findIndex(
        (part) => part.jid === action.payload.participant.jid,
      )

      if (participantIndex === -1) {
        state[chatIndex].participants.push(action.payload.participant)
      } else {
        state[chatIndex].participants[participantIndex] = {
          ...state[chatIndex].participants[participantIndex],
          ...action.payload.participant,
        }
      }
    },
    changeActiveChat: (
      state,
      action: PayloadAction<ChangeActiveChatAction>,
    ) => {
      const chatIndex = ChatHelpers.findChatIndex(
        action.payload.activeChatJid,
        state,
      )

      if (chatIndex === -1) {
        captureError(
          `Can not find chat with jid: ${action.payload.activeChatJid}`,
          { origin: "Other" },
        )
        return
      }

      // Find the currently active chat and deactivate it
      const activeIndex = state.findIndex((chat) => chat.active)
      if (activeIndex !== -1) {
        state[activeIndex].active = false
      }

      // Set the new chat as active
      state[chatIndex].active = true
    },
    upsertChat: (state, action: PayloadAction<UpsertChatAction>) => {
      const index = ChatHelpers.findChatIndex(action.payload.chat.jid, state)
      if (index === -1) {
        state.unshift(buildChat(action.payload.chat))
      } else {
        state[index] = { ...state[index], ...action.payload.chat }
      }
    },
    refreshChats: (state, action: PayloadAction<RefreshChatsAction>) => {
      const chatsThatExist = state.filter((chat) => {
        return ChatHelpers.findChatIndex(chat.jid, action.payload.chats) !== -1
      })

      state.splice(0, state.length)
      state.push(...chatsThatExist)

      const addNew = action.payload.chats.filter((chat) => {
        return ChatHelpers.findChatIndex(chat.jid, state) === -1
      })

      state.push(...addNew)

      for (const chat of action.payload.chats) {
        const index = ChatHelpers.findChatIndex(chat.jid, state)
        state[index] = {
          ...state[index],
          name: chat.name,
          groups: chat.groups ?? state[index].groups,
        }
      }
    },
    removeChat: (state, actions: PayloadAction<RemoveChatAction>) => {
      const chats = state.filter((chat) => {
        return chat.jid !== actions.payload.jid
      })
      state.splice(0, state.length)
      state.push(...chats)
    },
    dropPeekedChats: (state) => {
      for (let index = state.length - 1; index >= 0; index -= 1) {
        if (state[index].peek) state.splice(index, 1)
      }
    },
    updateChatConnectedStatus: (
      state,
      action: PayloadAction<{ jid: string; status: ChatConnectionStatus }>,
    ) => {
      const chat = state.find((chat) => chat.jid === action.payload.jid)
      if (!chat) return
      chat.connectionStatus = action.payload.status
    },

    updateChatName: (state, action: PayloadAction<UpdateChatNameAction>) => {
      const chatIndex = ChatHelpers.findChatIndex(action.payload.chatJid, state)
      if (chatIndex === -1) return

      state[chatIndex].name = action.payload.name
    },
  },
  extraReducers: (builder) => {
    builder.addCase(PURGE, () => initialState)
  },
})

export const {
  addOrUpdateParticipant,
  changeActiveChat,
  upsertChat,
  refreshChats,
  removeChat,
  dropPeekedChats,
  updateDraftMessage,
  updateChatConnectedStatus,
  updateChatName,
} = chatsSlice.actions
export default chatsSlice.reducer
